import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const tokenMissing = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (pwd !== confirm) { setError("Passwords don't match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      toast.success("Password updated — sign in with the new password");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f7f5f0]" data-testid="reset-password-page">
      <div className="flex flex-col px-8 sm:px-16 py-10 lg:py-20">
        <div className="font-display text-xl font-medium tracking-tight">
          Hearth<span className="text-[#c0612f]">.</span>
        </div>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#8a8476] mb-4">New password</div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-none">
              Set a new one.
            </h1>
            <p className="mt-4 text-sm text-[#57534e] leading-relaxed">
              Pick something at least 8 characters. You'll be redirected to sign-in straight after.
            </p>

            {tokenMissing ? (
              <div data-testid="reset-no-token" className="mt-8 bg-[#fbeae8] border border-[#f1c5be] text-[#a23a30] rounded-[3px] px-4 py-3 text-sm">
                This reset link is missing a token. Request a new one from the <Link to="/forgot-password" className="underline">Forgot password</Link> page.
              </div>
            ) : (
              <form className="space-y-5 mt-8" onSubmit={onSubmit} data-testid="reset-form">
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">New password</Label>
                  <Input type="password" required minLength={8} autoComplete="new-password" data-testid="reset-password-input" value={pwd} onChange={(e) => setPwd(e.target.value)} className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">Confirm new password</Label>
                  <Input type="password" required minLength={8} autoComplete="new-password" data-testid="reset-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-2 h-11 bg-white border-[#e5e2d9] rounded-[4px]" />
                </div>
                {error && (
                  <div data-testid="reset-error" className="text-sm text-[#d94f3d] bg-[#fbeae8] px-3 py-2 rounded-[3px]">{error}</div>
                )}
                <Button type="submit" disabled={busy} data-testid="reset-submit-button" className="w-full h-11 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] font-medium tracking-wide">
                  {busy ? "Saving…" : "Update password"}
                </Button>
              </form>
            )}

            <div className="text-sm text-[#57534e] mt-6">
              Remembered it? <Link to="/login" className="text-[#1b3b36] underline underline-offset-2">Sign in</Link>
            </div>
          </div>
        </div>
        <div className="text-xs text-[#8a8476]">© {new Date().getFullYear()} Hearth Operations</div>
      </div>
      <div className="relative hidden lg:block overflow-hidden grain">
        <img
          src="https://images.pexels.com/photos/7223295/pexels-photo-7223295.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1b3b36]/65 via-[#1b3b36]/25 to-transparent" />
      </div>
    </div>
  );
}
